import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, Search } from 'lucide-react';

/**
 * Searchable product picker using Combobox pattern.
 * @param {Object} props
 * @param {Array} props.products - Full product list [{id, product_name, product_code, category}]
 * @param {string|number} props.value - Selected product ID
 * @param {Function} props.onChange - (productId: string) => void
 * @param {boolean} [props.disabled] - Disable selection
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.testId] - data-testid
 * @param {Function} [props.onAfterSelect] - Fires after the user picks an item
 *        AND Radix has restored focus to the trigger. Used by the parent form
 *        to advance focus to the next field (e.g. Qty input) so Tab/Enter
 *        flows feel natural instead of jumping to the page header.
 */
export default function ProductPicker({ products, value, onChange, disabled, placeholder = 'Search product...', className = '', testId, onAfterSelect }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedProduct = useMemo(() => {
    if (!value) return null;
    return products.find(p => String(p.id) === String(value));
  }, [value, products]);

  const displayText = selectedProduct
    ? `${selectedProduct.product_code ? selectedProduct.product_code + ' — ' : ''}${selectedProduct.product_name}`
    : '';

  const BROWSE_LIMIT = 100;
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products.slice(0, BROWSE_LIMIT);
    const q = searchQuery.toLowerCase();
    return products.filter(p =>
      (p.product_name || '').toLowerCase().includes(q) ||
      (p.product_code || '').toLowerCase().includes(q)
    );
  }, [products, searchQuery]);

  const hasTruncatedBrowse = !searchQuery && products.length > BROWSE_LIMIT;

  const capitalise = (s) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between font-normal h-9 text-sm ${!selectedProduct ? 'text-muted-foreground' : ''} ${className}`}
          data-testid={testId}
        >
          <span className="truncate">{displayText || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or code..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>No products found.</CommandEmpty>
            <CommandGroup>
              {hasTruncatedBrowse && (
                <div className="px-3 py-1.5 text-xs text-muted-foreground border-b bg-slate-50">
                  Showing {BROWSE_LIMIT} of {products.length} products — type to search all
                </div>
              )}
              {filteredProducts.map((p) => (
                <CommandItem
                  key={p.id}
                  value={String(p.id)}
                  onSelect={(val) => {
                    onChange(val === String(value) ? '' : val);
                    setOpen(false);
                    setSearchQuery('');
                    // iter363b — Hand focus off to the parent's next field.
                    // We wait 60 ms so Radix has time to restore focus to
                    // the trigger first; then we override it. Without this
                    // the next Tab keypress hits the document body and
                    // jumps to the page header / logo.
                    if (onAfterSelect) setTimeout(onAfterSelect, 60);
                  }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${String(value) === String(p.id) ? 'opacity-100' : 'opacity-0'}`} />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm truncate">
                      {p.product_code && <span className="font-mono text-xs text-slate-500 mr-1.5">{p.product_code}</span>}
                      {p.product_name}
                    </span>
                    {/* iter363f — show subcategory + size/serving in
                        small grey type so auditors can disambiguate
                        same-named SKUs (e.g. Heineken 330 ml bottle
                        vs Heineken 500 ml can vs Heineken keg).  */}
                    {(() => {
                      const sub = p.subcategory || (typeof p.category === 'object' ? p.category?.label : p.category);
                      const baseUnit = p.units?.find(u => u.is_base_stock_unit);
                      const sizeBits = [];
                      if (baseUnit?.conversion_to_base && (baseUnit.base_unit || parseFloat(baseUnit.conversion_to_base) > 1)) {
                        sizeBits.push(`${baseUnit.conversion_to_base} ${baseUnit.base_unit || 'ml'}`);
                      } else if (baseUnit?.unit_type) {
                        sizeBits.push(baseUnit.unit_type);  // count-only unit: size unknown, show the unit not "1 ml"
                      }
                      if (p.case_size) sizeBits.push(`${p.case_size}/case`);
                      const hint = [sub, sizeBits.join(' · ')].filter(Boolean).join(' • ');
                      if (!hint) return null;
                      return (
                        <span className="text-[10px] text-muted-foreground truncate">{capitalise(hint)}</span>
                      );
                    })()}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
